import { Info, CheckCircle2, XCircle, AlertCircle, IdCard } from 'lucide-react'
import { CARD } from '@/utils/constant'
import { getImageByCardName } from '@/utils'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { openModal, closeModal } from '@/store/slices/modalSlice'
import { useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'

const statusConfig = {
    completed: {
        color: 'bg-green-100 text-green-800 border border-green-200',
        icon: <CheckCircle2 className="w-4 h-4 text-green-500" />
    },
    success: {
        color: 'bg-green-100 text-green-800 border border-green-200',
        icon: <CheckCircle2 className="w-4 h-4 text-green-500" />
    },
    failed: {
        color: 'bg-red-100/60 text-red-500 border border-red-100',
        icon: <XCircle className="w-4 h-4 text-red-500" />
    },
    error: {
        color: 'bg-red-100/60 text-red-500 border border-red-100',
        icon: <XCircle className="w-4 h-4 text-red-500" />
    },
    default: {
        color: 'bg-gray-100 text-gray-800 border border-gray-200',
        icon: <AlertCircle className="w-4 h-4 text-gray-500" />
    }
}

const getStatusConfig = (status: string) =>
    statusConfig[status.toLowerCase() as keyof typeof statusConfig] ??
    statusConfig.default

const IdSection = ({
    icon: Icon,
    label,
    value
}: {
    icon: React.ComponentType<any>;
    label: string;
    value: React.ReactNode;
}) => (
    <div className="flex items-center space-x-3">
        <Icon className="w-4 h-4 text-gray-400" />
        <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-500">{label}</p>
            <p className="font-mono text-sm truncate">{value}</p>
        </div>
    </div>
)

export default function TransactionDetailsModal({ data }: { data: any }) {

    const dispatch = useAppDispatch()

    const { isOpen, type, data: modalData } = useAppSelector(
        (s) => s.modal
    )

    const showModal = isOpen && type === 'transactionInfo' && modalData?._id === data._id

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('overflow-hidden')
        } else {
            document.body.classList.remove('overflow-hidden')
        }

        return () => {
            document.body.classList.remove('overflow-hidden')
        }
    }, [isOpen])


    return (
        <div className="flex items-center space-x-2 w-max">
            <button
                onClick={() =>
                    dispatch(openModal({ type: 'transactionInfo', data }))
                }
                className="p-2 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-all cursor-pointer"
                title="View Transaction Details"
            >
                <Info size={18} className="text-gray-600" />
            </button>

            <Dialog open={showModal} onOpenChange={() => dispatch(closeModal())}>
                <DialogContent className="sm:max-w-md" hideClose={false}>
                    <DialogHeader>
                        <DialogTitle>Transaction Details</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                        {/* Payment Method */}
                        <div className="w-full max-w-md">
                            {data.type.toLowerCase() === CARD.toLowerCase() && (
                                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                                    <img
                                        src={getImageByCardName(data.card_brand)}
                                        alt={data.card_brand}
                                        className="w-10 h-10 object-contain"
                                    />
                                    <div className="min-w-0">
                                        <p className="font-mono">**** **** **** {data.card_last4}</p>
                                        <p className="text-sm text-gray-600 truncate overflow-hidden">
                                            {data.card_username}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Amount + Status */}
                        <div className="flex items-center justify-between">
                            <div className="text-center">
                                <p className="text-sm text-gray-500">
                                    {data.status === 'success' ? 'Amount Paid' : 'Amount'}
                                </p>
                                <p className="text-2xl font-semibold mt-1">
                                    ${parseFloat(data.amount).toFixed(2)}
                                </p>
                            </div>

                            <div
                                className={`px-4 py-2 rounded-full flex items-center gap-2 ${getStatusConfig(
                                    data.status
                                ).color}`}
                            >
                                {getStatusConfig(data.status).icon}
                                <span className="font-medium capitalize">
                                    {data.status}
                                </span>
                            </div>
                        </div>

                        {/* Error message */}
                        {data.error_message && (
                            <div className="px-4 py-2 bg-red-100/60 rounded-lg border border-red-100">
                                <p className="text-red-500 break-words">{data.error_message}</p>
                            </div>
                        )}

                        {/* IDs */}
                        {(data.in_app_purchase_id ||
                            data.transaction_id ||
                            data.stripe_payment_id) && (
                            <div className="space-y-4 border-t pt-4">
                                <h3 className="text-sm font-medium text-gray-500">
                                        Transaction IDs
                                </h3>
                                <div className="space-y-3">
                                    {data.transaction_id && (
                                        <IdSection
                                            icon={IdCard}
                                            label="Transaction ID"
                                            value={data.transaction_id}
                                        />
                                    )}
                                    {data.stripe_payment_id && (
                                        <IdSection
                                            icon={IdCard}
                                            label="Stripe Payment ID"
                                            value={data.stripe_payment_id}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
